import type { AiMagicQuickControl } from '../../services/aiService'
import type {
  AiMagicQuestionSet,
} from '../../services/ai/interview/aiMagicQuestionSet'

/**
 * Synthesize a QuestionSet from legacy `quickControls` chips.
 *
 * Prior to the QuestionSet schema, the orchestrator emitted a flat list of
 * radio/checkbox chips optionally sharing a `group` field. This upgrader
 * groups them by that field so old chat history (and old assistant emissions)
 * render through the new premium interview card without asking the model to
 * re-serialize. Returns null when the chips can't be sensibly grouped
 * (button-only turn, empty list, etc.) — caller falls back to the legacy
 * chip strip in that case.
 */
export const upgradeQuickControlsToQuestionSet = (
  turnId: string | undefined,
  controls: AiMagicQuickControl[],
  assistantMessage?: string,
): AiMagicQuestionSet | null => {
  if (!controls || controls.length === 0) return null

  // Bucket by group; button-typed chips don't participate.
  const groups = new Map<string, AiMagicQuickControl[]>()
  for (const control of controls) {
    if (control.type === 'button') continue
    const key = control.group ?? 'choice'
    const bucket = groups.get(key) ?? []
    bucket.push(control)
    groups.set(key, bucket)
  }
  if (groups.size === 0) return null

  const questions: AiMagicQuestionSet['questions'] = []
  let index = 0
  for (const [groupKey, chips] of groups) {
    // Every chip in the same group should share a `type`; if they don't, fall
    // back to CHECKBOX (safer superset — a mis-labeled radio still functions).
    const allRadio = chips.every((chip) => chip.type === 'radio')
    const allCheckbox = chips.every((chip) => chip.type === 'checkbox')
    const type: 'RADIO' | 'CHECKBOX' = allRadio && !allCheckbox ? 'RADIO' : 'CHECKBOX'
    // Prompt heuristic: reuse the assistant's message on the first (usually
    // only) group; subsequent groups fall back to a generic label since the
    // legacy schema never carried a per-group prompt.
    const prompt = index === 0 && assistantMessage
      ? assistantMessage.replace(/\s+/g, ' ').trim()
      : groupKey.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    questions.push({
      id: groupKey || `q${index + 1}`,
      prompt,
      type,
      options: chips.map((chip) => ({ label: chip.label, value: chip.value })),
    })
    index += 1
  }

  return {
    id: turnId ? `legacy-${turnId}` : `legacy-${Date.now().toString(36)}`,
    questions,
  }
}
